import React, { useMemo } from 'react';
import Card from '../Card';
import { TimeChart, PALETTE, phasesOn } from '../charts';
import {
  chartRows, holderAt, lastValue, peakValue, replicaLabel, timeFormatter,
} from '../../observability/contract';
import { fmt } from './units';

// Copy N always gets the same colour, on every chart and in the copies table, so
// "the orange line" means the same copy everywhere on the page. PALETTE[0] is the
// service line's, so copies never borrow it. The order keeps the first copies far
// apart: in PALETTE order copy 4 would be red next to copy 2's burnt orange.
export const SERVICE_COLOR = PALETTE[0];
const COPY_ORDER = [1, 2, 3, 7, 11, 8, 6, 10, 5, 9, 4];
export const replicaColor = (index) => PALETTE[COPY_ORDER[(Math.max(1, index) - 1) % COPY_ORDER.length]];

/**
 * The per-chart copy picker. Rendered only when there is a choice to make: a
 * single-copy service shows a plain caption instead of a one-option menu.
 */
export function ReplicaSelect({ replicas, value, onChange, aggregate, chartName }) {
  if (!replicas || replicas.length < 2) {
    return (
      <span className="text-xs text-muted nowrap">
        {replicas?.length === 1 ? '1 copy' : 'No copy in this window'}
      </span>
    );
  }
  return (
    <select
      className="form-input chart-select"
      aria-label={`Copy shown on ${chartName}`}
      value={value}
      onChange={e => onChange(e.target.value)}
    >
      <option value="service">All copies ({aggregate === 'sum' ? 'total' : 'average'})</option>
      {replicas.map(r => (
        <option key={r.id} value={r.id}>{replicaLabel(r)}</option>
      ))}
      <option value="compare">Compare copies</option>
    </select>
  );
}

// `offline`: the series belongs to a copy no task holds now, so its last value is
// history, not "now". `past`: the window ended before now (a load test's), so its last
// value is the one at its end, whatever the copy does today.
function Readout({ unit, values, offline, past }) {
  const f = fmt(unit);
  const off = offline && !past;
  const now = off ? null : lastValue(values);
  const peak = peakValue(values);
  if (now === null && peak === null && !off) return null;
  return (
    <div className="metric-readout">
      <span>{past ? 'At the end' : 'Now'} {off
        ? <strong>offline</strong>
        : <strong className="num">{now === null ? '—' : f.value(now)}</strong>}</span>
      <span>Peak <strong className="num">{peak === null ? '—' : f.value(peak)}</strong></span>
    </div>
  );
}

/**
 * One service metric (a contract MetricBlock) with its own copy selector.
 * `view` is 'service' | <copy id> | 'compare' and is owned by the caller, so a
 * panel-level control can move every chart at once while each chart can still be
 * changed on its own.
 */
export default function MetricCard({
  title, subtitle, help, block, replicas = [], view = 'service', onViewChange,
  range, yMax, refLines, height = 220, span = 6, loading, formatX, timeline, past = false,
}) {
  const f = fmt(block?.unit);
  const { rows, lines } = useMemo(() => chartRows(block, replicas, view), [block, replicas, view]);
  const phases = useMemo(() => phasesOn(rows, timeline), [rows, timeline]);
  const copyOf = key => replicas.find(x => x.id === key);
  const colors = lines.map(l => (l.key === 'service' ? SERVICE_COLOR : replicaColor(copyOf(l.key)?.index ?? 1)));
  // Why a point is missing: no task held the copy then ("offline"), or one did and
  // reported nothing ("no data"). The copies' task spans answer it.
  const ts = block?.timestamps || [];
  const stepMs = ts.length > 1 ? ts[1] - ts[0] : 60 * 1000;
  const gapLabel = (key, i) => {
    const at = ts[i];
    if (at == null) return 'no data';
    if (key === 'service') return replicas.some(r => holderAt(r, at, stepMs)) ? 'no data' : 'offline';
    const copy = copyOf(key);
    if (!copy) return 'no data';
    return holderAt(copy, at, stepMs) ? 'no data' : 'offline';
  };
  const single = lines.length === 1 ? block?.series?.[lines[0].key] : null;
  const singleCopy = lines.length === 1 ? copyOf(lines[0].key) : null;
  return (
    <Card
      title={title}
      subtitle={subtitle}
      help={help}
      span={span}
      loading={loading}
      empty={!loading && !block ? 'Not reported for this service' : null}
      actions={onViewChange && block ? (
        <ReplicaSelect replicas={replicas} value={view} onChange={onViewChange}
                       aggregate={block.aggregate} chartName={title} />
      ) : null}
    >
      <TimeChart
        data={rows} labelKey="t" lines={lines} colors={colors} height={height}
        formatY={f.axis} formatValue={f.value} formatX={formatX || timeFormatter(range)}
        yMax={yMax} refLines={refLines} gapOnNull gapLabel={gapLabel} legend={lines.length > 1}
        phases={phases}
      />
      {single && <Readout unit={block.unit} values={single} offline={singleCopy?.status === 'STOPPED'}
                          past={past} />}
    </Card>
  );
}

/**
 * A chart over a plain `{ timestamps, series }` payload (database, cluster, load
 * balancer): no copies, one line per `lines[]` entry.
 */
export function SeriesCard({
  title, subtitle, help, timestamps, series, lines, unit = 'count', range,
  yMax, refLines, height = 220, span = 6, loading, readoutKey, actions, formatX, timeline,
  past = false,
}) {
  const f = fmt(unit);
  const rows = useMemo(() => (timestamps || []).map((ts, i) => {
    const row = { t: new Date(ts).toISOString() };
    lines.forEach((l) => {
      const v = series?.[l.key]?.[i];
      row[l.key] = v === undefined ? null : v;
    });
    return row;
  }), [timestamps, series, lines]);
  const phases = useMemo(() => phasesOn(rows, timeline), [rows, timeline]);
  const colors = lines.map((l, i) => l.color || PALETTE[i % PALETTE.length]);
  const readout = readoutKey || (lines.length === 1 ? lines[0].key : null);
  // Live series can be all gaps: a metric not published yet, or a sampler that started
  // recording after the start of the window. An empty axis would read as "zero".
  const noData = rows.every(r => lines.every(l => r[l.key] == null));
  return (
    <Card title={title} subtitle={subtitle} help={help} span={span} loading={loading} actions={actions}
          empty={!loading && noData ? 'No datapoints in this window' : null}>
      <TimeChart
        data={rows} labelKey="t" lines={lines.map(({ key, label }) => ({ key, label }))}
        colors={colors} height={height} formatY={f.axis} formatValue={f.value}
        formatX={formatX || timeFormatter(range)} yMax={yMax} refLines={refLines} gapOnNull
        legend={lines.length > 1} phases={phases}
      />
      {readout && <Readout unit={unit} values={series?.[readout]} past={past} />}
    </Card>
  );
}
